import React from "react";
import { Box, Text } from "@chakra-ui/react";
import { FaTrophy } from "react-icons/fa";

/**
 * The contents of a leaderboard's rank cell.
 *
 * Once a pool has finished the trophy *replaces* the number rather than
 * sitting beside it: a "1" says nothing a trophy hasn't already said, and the
 * frozen rank column is only a few pixels wide on a phone.
 *
 * Every game ranks densely — genuinely tied players share a rank — so a shared
 * first place gives each of them rank 1, and each gets a trophy.
 *
 * `isWinner` is for the two boards whose rank column shows a row's position
 * under the reader's chosen sort rather than its standing. There the number on
 * screen isn't the standing rank, so who won has to be stated outright.
 */
export default function WinnerRank({ rank, finished, isWinner, size, placeholder = "-" }) {
  const won = finished && (isWinner === undefined ? rank === 1 : isWinner);
  if (won) {
    return (
      <Box
        as="span"
        color="yellow.500"
        lineHeight="1"
        display="inline-flex"
        title="Pool winner"
        aria-label="Pool winner"
      >
        <FaTrophy size={size} />
      </Box>
    );
  }
  return <Text as="span">{rank ?? placeholder}</Text>;
}

/**
 * A player's name on a board that has no rank column to replace.
 *
 * The two predictor grids — PL's table predictor and NFL's division predictor —
 * run a column per player and score them, so the winner is marked on the name
 * itself. Returns a fragment rather than its own flex row, so it drops into
 * whatever HStack the caller already has without nesting one inside another.
 */
export function WinnerName({ name, isWinner, size, fontSize, noOfLines }) {
  return (
    <>
      {isWinner && (
        <Box
          as="span"
          color="yellow.500"
          lineHeight="1"
          display="inline-flex"
          flexShrink={0}
          title="Winner"
          aria-label="Winner"
        >
          <FaTrophy size={size} />
        </Box>
      )}
      <Text as="span" fontSize={fontSize} noOfLines={noOfLines}>
        {name}
      </Text>
    </>
  );
}
